import { call, put, select, takeLatest } from 'redux-saga/effects'
import { ATTACH, BENCH, EVOLVE, PUT_ACTIVE_POKEMON, SWITCH_IN } from './constants';
import { endAttach, endBench, endEvolve, startAttach, startBench, startEvolve, endPutBenchAsActive, endSwitchIn } from './actions';
import { selectAttachCardPosition, selectBenchPosition, selectEvolveCardPosition } from './selectors';
import gameTurnService from '../services/gameTurn';
import { handleUpdateGame } from '../ActiveGame/sagas';
import { closeContextMenu } from '../ActiveGame/actions';
import { selectActiveGame, selectActivePokemon, selectBench, selectHand } from '../ActiveGame/selectors';
import { selectCurrentUser } from '../Login/selectors';
import { waitForUndo } from '../Undo/waitForUndo';
import { attachWaits } from '../Undo/undoableMoves';
import { undoDone } from '../Undo/actions';
import { selectHandPosition } from '../Hand/selectors';

// Each task ends its flow even when the server refuses the move, so the board never stays stuck waiting for a choice.

// Attaching is once a turn; when it waits with an Undo first is up to Undo/undoableMoves.
function* attachTask(action) {
  yield put(startAttach());
  const energyPosition = yield select(selectAttachCardPosition);
  const benchPosition = action.payload.get('benchPosition');
  const gameId = (yield select(selectActiveGame)).get('id');

  try {
    const userId = (yield select(selectCurrentUser)).get('id');
    const hand = yield select(selectHand, { userId });
    const energy = hand && hand.get('cards').find(card => card.get('order') === energyPosition);
    const target = benchPosition === null
      ? (yield select(selectActivePokemon, { userId }))?.get('card')
      : (yield select(selectBench, { userId }))?.get('cards').find(card => card.get('order') === benchPosition);
    const words = `${energy ? energy.get('name') : 'The energy'} goes on ${target ? target.get('name') : 'your Pokémon'}`;
    const undoTitle = attachWaits({ dragged: action.payload.get('dragged') });
    // Dropped there, it shows on the Pokémon while you can still undo it (AttachPreview).
    const attach = { userId, benchPosition, handPosition: energyPosition, card: energy };
    if (undoTitle && (yield call(waitForUndo, undoTitle, words, { attach }))) {
      yield put(closeContextMenu());
      return;
    }
    const game = yield call(gameTurnService.attach, gameId, energyPosition, benchPosition);
    yield call(handleUpdateGame, game);
    yield put(closeContextMenu());
  } finally {
    yield put(undoDone());
    yield put(endAttach());
  }
}

function* attachWatcher() {
  yield takeLatest(ATTACH, attachTask);
}

function* evolveTask(action) {
  yield put(startEvolve());
  const handPosition = yield select(selectEvolveCardPosition);
  const benchPosition = action.payload.get('benchPosition');
  const gameId = (yield select(selectActiveGame)).get('id');

  try {
    const game = yield call(gameTurnService.evolve, gameId, handPosition, benchPosition);
    yield call(handleUpdateGame, game);
    yield put(closeContextMenu());
  } finally {
    yield put(endEvolve());
  }
}

function* evolveWatcher() {
  yield takeLatest(EVOLVE, evolveTask);
}

function* benchTask() {
  yield put(startBench());
  const handPosition = yield select(selectHandPosition);
  const benchPosition = yield select(selectBenchPosition);
  const gameId = (yield select(selectActiveGame)).get('id');

  try {
    const game = yield call(gameTurnService.bench, gameId, handPosition, benchPosition);
    yield call(handleUpdateGame, game);
    yield put(closeContextMenu());
  } finally {
    yield put(endBench());
  }
}

function* benchWatcher() {
  yield takeLatest(BENCH, benchTask);
}

function* putActivePokemonTask(action) {
  const gameId = (yield select(selectActiveGame)).get('id');

  try {
    const game = yield call(gameTurnService.putBenchAsActivePokemon, gameId, action.payload.get('benchCardPosition'));
    yield call(handleUpdateGame, game);
    yield put(closeContextMenu());
  } finally {
    yield put(endPutBenchAsActive());
  }
}

function* putActivePokemonWatcher() {
  yield takeLatest(PUT_ACTIVE_POKEMON, putActivePokemonTask);
}

function* switchInTask(action) {
  const gameId = (yield select(selectActiveGame)).get('id');

  try {
    const game = yield call(gameTurnService.switchIn, gameId, action.payload.get('benchPosition'));
    yield call(handleUpdateGame, game);
    yield put(closeContextMenu());
  } finally {
    yield put(endSwitchIn());
  }
}

function* switchInWatcher() {
  yield takeLatest(SWITCH_IN, switchInTask);
}

export default [
  evolveWatcher,
  attachWatcher,
  benchWatcher,
  putActivePokemonWatcher,
  switchInWatcher,
];
